import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, Phone, Mail, Instagram } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { useLang } from "@/context/LanguageContext";

export const Contact = () => {
  const { content } = useContent();
  const { t } = useLang();
  const c = content?.contact || {};
  const waNumber = (c.whatsapp || "").replace(/[^0-9]/g, "");
  const waLink = `https://wa.me/${waNumber}`;

  const cards = [
    { icon: MessageCircle, label: t("contact.whatsapp"), value: c.whatsapp, href: waLink, external: true, testid: "contact-whatsapp", accent: "bg-[#25D366]" },
    { icon: Phone, label: t("contact.phone"), value: c.phone, href: `tel:${c.phone}`, testid: "contact-phone", accent: "bg-ocean" },
    { icon: Mail, label: t("contact.email"), value: c.email, href: `mailto:${c.email}`, testid: "contact-email", accent: "bg-gold" },
    { icon: Instagram, label: t("contact.instagram"), value: c.instagram, href: c.instagram_url, external: true, testid: "contact-instagram", accent: "bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737]" },
  ];

  return (
    <section id="contact" className="bg-sand-200 py-20 sm:py-28" data-testid="contact-section">
      <div className="max-w-5xl mx-auto px-5 sm:px-6 text-center">
        <p className="eyebrow text-coral mb-4">{t("contact.eyebrow")}</p>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-ocean leading-[1.05] mb-4">
          {t("contact.title")}
        </h2>
        <p className="text-ocean/60 max-w-xl mx-auto mb-12">
          {t("contact.sub")}
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((card, i) => {
            const Icon = card.icon;
            return (
              <motion.a
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                href={card.href || "#"}
                target={card.external ? "_blank" : undefined}
                rel={card.external ? "noreferrer" : undefined}
                data-testid={card.testid}
                className="bg-white rounded-2xl p-6 border border-ocean/10 flex flex-col items-center gap-3 hover:-translate-y-1 hover:shadow-xl transition-all"
              >
                <div className={`w-14 h-14 rounded-full ${card.accent} text-white flex items-center justify-center`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-ocean/50">{card.label}</div>
                <div className="font-heading font-bold text-ocean text-sm break-all">{card.value}</div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
};
